import type { CashflowTarget } from '@moment/horizon-data-access/proposal'

import { IsoDateStr } from '@moment/domain-types'
import { parseIsoDate } from '@moment/utils/datetime'
import { Temporal } from 'temporal-polyfill'

import { parseStoredGranularity, windowMonths } from './granularity'
import { type BoundIssue, targetBoundIssues } from './target-bounds'

// Mirrors portfolios suboptimal_core.cashflow's planner; the golden JSON pins both.

// Portfolios plans on the UTC date, so the UI's "today" for planning is too.
export const PLANNING_TIME_ZONE = 'UTC'

type RecurringFrequency = Exclude<CashflowTarget['frequency'], 'one_time'>

const PERIOD_MONTHS: Record<RecurringFrequency, number> = {
  monthly: 1,
  bimonthly: 2,
  quarterly: 3,
  semiannually: 6,
  annually: 12,
}

type RecurringTarget = CashflowTarget & { frequency: RecurringFrequency }

interface RecurringEntry {
  index: number
  target: RecurringTarget
}

export function isRecurring(target: CashflowTarget): target is RecurringTarget {
  return target.frequency !== 'one_time'
}

function recurringEntries(targets: CashflowTarget[]): RecurringEntry[] {
  const entries: RecurringEntry[] = []
  targets.forEach((target, index) => {
    if (isRecurring(target)) entries.push({ index, target })
  })
  return entries
}

function periodMonths(target: RecurringTarget): number {
  return PERIOD_MONTHS[target.frequency]
}

interface BucketWindow {
  start: Temporal.PlainDate
  end: Temporal.PlainDate
}

const toKey = (date: Temporal.PlainDate) => date.toString().replaceAll('-', '')

// The backend rejects invalid windows outright; reading them as the whole period keeps this from throwing.
function targetWindowMonths(target: RecurringTarget): number {
  return windowMonths(target.frequency, parseStoredGranularity(target))
}

// A stored date is never checked at runtime, so an unreadable one reads as no date instead of throwing.
function readDate(value: string | undefined): Temporal.PlainDate | null {
  const parsed = IsoDateStr.safeParse(value)
  return parsed.success ? parseIsoDate(parsed.data) : null
}

interface WindowsFrom {
  // Skip windows that close by this day.
  from?: Temporal.PlainDate
}

/** Every [start, end) window the target covers (one for one_time); none without a readable start and end. */
function* windowsOf(
  target: CashflowTarget,
  { from }: WindowsFrom = {},
): Generator<BucketWindow> {
  const start = readDate(target.start_date)
  const end = readDate(target.end_date)
  if (!start || !end) return
  if (!isRecurring(target)) {
    yield { start, end }
    return
  }
  const period = periodMonths(target)
  const width = targetWindowMonths(target)
  // Windows before `first` all close by `from`, since month adds never decrease.
  const monthsToFrom = from
    ? start.until(from, { largestUnit: 'months' }).months
    : 0
  const first = Math.max(0, Math.floor((monthsToFrom - width) / period))
  for (let i = first; ; i++) {
    // Adding from the start (not the previous bucket) keeps month-end clamping from drifting.
    const current = start.add({ months: i * period })
    if (Temporal.PlainDate.compare(current, end) >= 0) return
    const close = start.add({ months: i * period + width })
    yield {
      start: current,
      end: Temporal.PlainDate.compare(close, end) < 0 ? close : end,
    }
  }
}

const bucketWindows = (target: CashflowTarget) => Array.from(windowsOf(target))

/** Portfolios' is_live: a window still ends after `dt`, and an open start runs from `dt`. */
export function isLive(target: CashflowTarget, dt: IsoDateStr): boolean {
  if (target.end_date <= dt) return false
  if (!target.start_date) return true
  // Starts near `dt` and stops at the first window past it, so far dates cost nothing extra.
  for (const window of windowsOf(target, { from: parseIsoDate(dt) })) {
    if (window.end.toString() > dt) return true
  }
  return false
}

// Exact-key lookup, like portfolios' resolve_bucket_amount; a legacy range key is ignored.
function resolveAmount(
  target: CashflowTarget,
  key: string,
): number | undefined {
  return target.overrides?.[key] ?? target.amount
}

// Per-window stacking, index = position; mirrors portfolios' plan_cashflow_windows.

type Source = CashflowTarget['source']
type CallAssumption = CashflowTarget['call_assumption']

export interface PlannedBucket {
  start: IsoDateStr
  end: IsoDateStr
  amount: number | null
  contributors: number[]
  banded: boolean
  source: Source
  callAssumption: CallAssumption
}

type OverlapCode = 'overlapping_windows' | 'shared_bucket_start'

export type StackingIssue =
  | BoundIssue
  | {
      code:
        | 'mixed_one_time'
        | 'missing_start'
        | 'amount_required'
        | 'amount_not_positive'
      index: number
    }
  | {
      code: 'shared_bucket_start'
      index: number
      other: number
      start: IsoDateStr
      // The two targets draw on different sources or call assumptions.
      crossGroup: boolean
    }
  | {
      code: 'overlapping_windows'
      index: number
      other: number
      start: IsoDateStr
      // The flagged window and the other target's window it conflicts with, for fix hints.
      ownStart: IsoDateStr
      ownEnd: IsoDateStr
      otherStart: IsoDateStr
      otherEnd: IsoDateStr
      // Whether each window was cut short by its target's end_date.
      ownClipped: boolean
      otherClipped: boolean
      // A row of the pair whose start day is past the 28th, so its windows clamp to month ends.
      monthEndRow: { index: number; day: number } | null
      // The two targets draw on different sources or call assumptions, so they may not overlap at all.
      crossGroup: boolean
    }

const ISSUE_ORDER: StackingIssue['code'][] = [
  'amount_out_of_range',
  'override_out_of_range',
  'date_out_of_range',
  'start_not_before_end',
  'mixed_one_time',
  'missing_start',
  'amount_required',
  'amount_not_positive',
  'overlapping_windows',
  'shared_bucket_start',
]

interface PlanWindow {
  index: number
  start: string
  end: string
  group: string
  amount: number | null
  overridden: boolean
}

interface PlanBucket {
  start: string
  end: string
  group: string
  source: Source
  callAssumption: CallAssumption
  windows: PlanWindow[]
  // The distinct targets whose windows form this bucket.
  indexes: number[]
}

interface Conflict {
  code: OverlapCode
  index: number
  own: [string, string]
  other: number
  theirs: [string, string]
  crossGroup: boolean
}

// ISO dates compare lexicographically the same as chronologically.
function compareStr(a: string, b: string): number {
  if (a === b) return 0
  return a < b ? -1 : 1
}

function compareTuple(a: (string | number)[], b: (string | number)[]): number {
  for (let k = 0; k < Math.min(a.length, b.length); k++) {
    const x = a[k]
    const y = b[k]
    if (x === y) continue
    return typeof x === 'number' && typeof y === 'number'
      ? x - y
      : compareStr(String(x), String(y))
  }
  return a.length - b.length
}

/** Higher is less frequent; of two conflicting targets, the higher one is flagged. */
function rank(target: CashflowTarget): (string | number)[] {
  const recurring = isRecurring(target)
  return [
    recurring ? periodMonths(target) : Infinity,
    recurring ? targetWindowMonths(target) : 0,
    target.start_date ?? '',
    target.end_date,
  ]
}

// Each target's place in rank order, tied ranks sharing one, so a pair compares as two numbers.
function rankPositions(targets: CashflowTarget[]): number[] {
  const ranks = targets.map(rank)
  const order = targets
    .map((_, i) => i)
    .sort((i, j) => compareTuple(ranks[i] ?? [], ranks[j] ?? []))
  const positions: number[] = []
  order.forEach((index, k) => {
    const prev = order[k - 1]
    const tied =
      prev !== undefined &&
      compareTuple(ranks[prev] ?? [], ranks[index] ?? []) === 0
    positions[index] = tied ? (positions[prev] ?? k) : k
  })
  return positions
}

// The order keepFirst picks a target's earliest conflict by: own window, their window, then their index.
function compareConflicts(a: Conflict, b: Conflict): number {
  return (
    compareStr(a.own[0], b.own[0]) ||
    compareStr(a.own[1], b.own[1]) ||
    compareStr(a.theirs[0], b.theirs[0]) ||
    compareStr(a.theirs[1], b.theirs[1]) ||
    a.other - b.other
  )
}

// Only each target's earliest conflict per code is reported, so only that is kept, as portfolios does.
type FirstConflicts = Map<string, Conflict>

function keepFirst(first: FirstConflicts, c: Conflict) {
  const key = `${c.index}|${c.code}`
  const held = first.get(key)
  if (held === undefined || compareConflicts(c, held) < 0) first.set(key, c)
}

interface ConflictPair {
  code: OverlapCode
  a: PlanBucket
  b: PlanBucket
}

function keepConflictsBetween(
  { code, a, b }: ConflictPair,
  ranks: number[],
  first: FirstConflicts,
) {
  const aSpan: [string, string] = [a.start, a.end]
  const bSpan: [string, string] = [b.start, b.end]
  const crossGroup = a.group !== b.group
  for (const i of a.indexes) {
    for (const j of b.indexes) {
      const ri = ranks[i]
      const rj = ranks[j]
      if (i === j || ri === undefined || rj === undefined) continue
      const order = ri - rj
      if (order >= 0) {
        keepFirst(first, {
          code,
          index: i,
          own: aSpan,
          other: j,
          theirs: bSpan,
          crossGroup,
        })
      }
      if (order <= 0) {
        keepFirst(first, {
          code,
          index: j,
          own: bSpan,
          other: i,
          theirs: aSpan,
          crossGroup,
        })
      }
    }
  }
}

function stackedAmount(windows: PlanWindow[]): number | null {
  const amounts = windows.flatMap((w) => (w.amount === null ? [] : [w.amount]))
  if (!amounts.length) return null
  return amounts.reduce((sum, amount) => sum + amount, 0)
}

const byIssueOrder = (
  a: { index: number; code: StackingIssue['code'] },
  b: { index: number; code: StackingIssue['code'] },
) =>
  a.index - b.index || ISSUE_ORDER.indexOf(a.code) - ISSUE_ORDER.indexOf(b.code)

function amountIssues(targets: CashflowTarget[]): StackingIssue[] {
  const recurring = targets.flatMap((target, index) =>
    isRecurring(target) ? [{ index, months: periodMonths(target) }] : [],
  )
  const shortest = Math.min(...recurring.map((r) => r.months))
  const tied = recurring.filter((r) => r.months === shortest)
  const exempt = tied.length === 1 ? tied[0]?.index : undefined
  return targets.flatMap((target, index): StackingIssue[] => {
    if (!isRecurring(target)) return []
    if (index === exempt && (target.amount == null || target.amount === 0))
      return []
    if (target.amount == null) return [{ code: 'amount_required', index }]
    if (target.amount <= 0) return [{ code: 'amount_not_positive', index }]
    return []
  })
}

function earlyIssues(targets: CashflowTarget[]): StackingIssue[] {
  const mixed = targets.flatMap((target, index): StackingIssue[] =>
    isRecurring(target) ? [] : [{ code: 'mixed_one_time', index }],
  )
  if (mixed.length) return mixed
  return targets.flatMap((target, index): StackingIssue[] =>
    target.start_date ? [] : [{ code: 'missing_start', index }],
  )
}

function planWindows(targets: CashflowTarget[]): PlanWindow[] {
  return targets.flatMap((target, index) =>
    bucketWindows(target).map((window) => {
      const override = target.overrides?.[toKey(window.start)]
      return {
        index,
        start: window.start.toString(),
        end: window.end.toString(),
        group: `${target.source}|${target.call_assumption}`,
        amount: resolveAmount(target, toKey(window.start)) ?? null,
        overridden: override !== undefined && override !== null,
      }
    }),
  )
}

function groupBuckets(
  targets: CashflowTarget[],
  windows: PlanWindow[],
  stacking: boolean,
): PlanBucket[] {
  // Identical windows in one group stack only in stacking mode; otherwise each stays its own bucket.
  const keyed = new Map<string, PlanWindow[]>()
  for (const w of windows) {
    const key = `${stacking ? w.group : w.index}|${w.start}|${w.end}`
    const same = keyed.get(key)
    if (same) same.push(w)
    else keyed.set(key, [w])
  }
  const buckets = [...keyed.values()].flatMap((ws): PlanBucket[] => {
    const [first] = ws
    const target = first && targets[first.index]
    if (!first || !target) return []
    return [
      {
        start: first.start,
        end: first.end,
        group: first.group,
        source: target.source,
        callAssumption: target.call_assumption,
        windows: ws,
        indexes: [...new Set(ws.map((w) => w.index))],
      },
    ]
  })
  return buckets.sort((a, b) =>
    compareTuple(
      [a.start, a.end, a.source, a.callAssumption],
      [b.start, b.end, b.source, b.callAssumption],
    ),
  )
}

function findConflicts(
  targets: CashflowTarget[],
  buckets: PlanBucket[],
  stacking: boolean,
  dt: IsoDateStr | undefined,
): Conflict[] {
  // A window ended by dt is never floored or banded, so it cannot conflict.
  const current = buckets.filter((b) => dt === undefined || b.end > dt)
  const ranks = rankPositions(targets)
  const first: FirstConflicts = new Map()
  if (stacking) {
    for (let k = 0; k < current.length; k++) {
      const a = current[k]
      if (!a) continue
      for (let m = k + 1; m < current.length; m++) {
        const b = current[m]
        if (!b || b.start >= a.end) break
        const pair = { code: 'overlapping_windows' as const, a, b }
        keepConflictsBetween(pair, ranks, first)
      }
    }
  }
  // Stacked windows sharing a start already overlap, so only a lone recurring target needs this.
  if (!stacking && targets.some(isRecurring)) {
    const byStart = new Map<string, PlanBucket[]>()
    for (const b of current) {
      const same = byStart.get(b.start)
      if (same) same.push(b)
      else byStart.set(b.start, [b])
    }
    for (const same of byStart.values()) {
      for (let k = 0; k < same.length; k++) {
        for (let m = k + 1; m < same.length; m++) {
          const a = same[k]
          const b = same[m]
          if (!a || !b) continue
          const pair = { code: 'shared_bucket_start' as const, a, b }
          keepConflictsBetween(pair, ranks, first)
        }
      }
    }
  }
  return [...first.values()]
}

// The first of `indexes` whose start day is past the 28th, so its windows clamp to month ends.
function monthEndRow(
  targets: CashflowTarget[],
  indexes: number[],
): { index: number; day: number } | null {
  for (const index of indexes) {
    const day = readDate(targets[index]?.start_date)?.day ?? 0
    if (day > 28) return { index, day }
  }
  return null
}

/** Each kept conflict as an issue, sorted by (index, code order); findConflicts keeps one per target and code. */
function toIssues(
  conflicts: Conflict[],
  targets: CashflowTarget[],
): StackingIssue[] {
  return [...conflicts].sort(byIssueOrder).map((c): StackingIssue => {
    const start = IsoDateStr.parse(c.own[0])
    const shared = { index: c.index, other: c.other, start }
    if (c.code === 'shared_bucket_start') {
      return { code: c.code, ...shared, crossGroup: c.crossGroup }
    }
    return {
      code: c.code,
      ...shared,
      ownStart: start,
      ownEnd: IsoDateStr.parse(c.own[1]),
      otherStart: IsoDateStr.parse(c.theirs[0]),
      otherEnd: IsoDateStr.parse(c.theirs[1]),
      ownClipped: c.own[1] === targets[c.index]?.end_date,
      otherClipped: c.theirs[1] === targets[c.other]?.end_date,
      monthEndRow: monthEndRow(targets, [c.index, c.other]),
      crossGroup: c.crossGroup,
    }
  })
}

export interface PlanOptions {
  // Targets without a window ending after this day drop out, and its ended buckets never conflict or band; without it all are live.
  dt?: IsoDateStr
}

export interface CashflowPlan {
  buckets: PlannedBucket[]
  issues: StackingIssue[]
}

function remapIssue(issue: StackingIssue, back: number[]): StackingIssue {
  const index = back[issue.index] ?? issue.index
  if (issue.code === 'shared_bucket_start') {
    return { ...issue, index, other: back[issue.other] ?? issue.other }
  }
  if (issue.code !== 'overlapping_windows') return { ...issue, index }
  const row = issue.monthEndRow
  const monthEndRow = row && { ...row, index: back[row.index] ?? row.index }
  const other = back[issue.other] ?? issue.other
  return { ...issue, index, other, monthEndRow }
}

// Portfolios' live_targets: an open start runs from dt, except in a stack (refused as missing_start).
// An all-open one_time list takes the backend's cumulative transform instead, which stays there.
function withOpenStartsOn(
  targets: CashflowTarget[],
  dt: IsoDateStr,
): CashflowTarget[] {
  const openOneTimeOnly = targets.every((t) => !isRecurring(t) && !t.start_date)
  const stacking = targets.filter(isRecurring).length >= 2
  if (openOneTimeOnly || stacking) return targets
  return targets.map((t) => (t.start_date ? t : { ...t, start_date: dt }))
}

/** Portfolios' plan_cashflow_buckets unelapsed step (with `dt`) or plan_cashflow_windows (without), in submitted row positions. */
export function planCashflowBuckets(
  targets: CashflowTarget[],
  { dt }: PlanOptions = {},
): CashflowPlan {
  // Portfolios refuses these before planning, and a far end date would plan thousands of windows.
  const bounds = targetBoundIssues(targets)
  if (bounds.length) return { buckets: [], issues: bounds }
  if (dt === undefined) return planRows(targets, dt)
  const back = targets.flatMap((t, i) => (isLive(t, dt) ? [i] : []))
  const live = back.map((i) => targets[i]).filter((t) => t !== undefined)
  const plan = planRows(withOpenStartsOn(live, dt), dt)
  return {
    buckets: plan.buckets.map((b) => ({
      ...b,
      contributors: b.contributors.map((k) => back[k] ?? k),
    })),
    issues: plan.issues.map((issue) => remapIssue(issue, back)),
  }
}

function planRows(
  targets: CashflowTarget[],
  dt: IsoDateStr | undefined,
): CashflowPlan {
  const stacking = targets.filter(isRecurring).length >= 2
  if (stacking) {
    const early = earlyIssues(targets)
    if (early.length) return { buckets: [], issues: early }
  }

  const buckets = groupBuckets(targets, planWindows(targets), stacking)
  const issues = [
    ...toIssues(findConflicts(targets, buckets, stacking, dt), targets),
    ...(stacking ? amountIssues(targets) : []),
  ].sort(byIssueOrder)
  if (issues.length) return { buckets: [], issues }

  const contributorsOf = (b: PlanBucket) =>
    b.windows.map((w) => w.index).sort((x, y) => x - y)
  const bandable = (b: PlanBucket) => {
    const live = dt === undefined || b.end > dt
    return (
      live &&
      b.windows.every((w) => {
        const target = targets[w.index]
        return !w.overridden && target !== undefined && isRecurring(target)
      })
    )
  }
  // A band covers one combination of contributors, so it needs two live, unoverridden buckets.
  const combos = new Map<string, number>()
  for (const b of buckets) {
    if (!bandable(b)) continue
    const key = contributorsOf(b).join(',')
    combos.set(key, (combos.get(key) ?? 0) + 1)
  }
  return {
    buckets: buckets.map((b) => {
      const contributors = contributorsOf(b)
      const comboSize = combos.get(contributors.join(',')) ?? 0
      return {
        start: IsoDateStr.parse(b.start),
        end: IsoDateStr.parse(b.end),
        amount: stackedAmount(b.windows),
        contributors,
        banded: bandable(b) && comboSize >= 2,
        source: b.source,
        callAssumption: b.callAssumption,
      }
    }),
    issues: [],
  }
}

// Sorts after every real ISO date string, so a missing start ranks last.
const NO_START_RANK = '9999-99-99'

/** The recurring target the UI lists first: live on `dt` if any is, then finest cadence, earliest start, lowest index. */
export function primaryTargetIndex(
  targets: CashflowTarget[],
  { dt }: PlanOptions = {},
): number | null {
  const all = recurringEntries(targets)
  const live =
    dt === undefined ? [] : all.filter(({ target }) => isLive(target, dt))
  const recurring = live.length ? live : all
  const key = ({ target }: RecurringEntry) => [
    periodMonths(target),
    target.start_date ?? NO_START_RANK,
  ]
  const best = recurring.reduce<RecurringEntry | null>(
    (pick, entry) =>
      pick === null || compareTuple(key(entry), key(pick)) < 0 ? entry : pick,
    null,
  )
  return best?.index ?? null
}

interface StackOptions extends PlanOptions {
  // An existing plan of these same targets and dt, so the caller's plan is reused.
  plan?: CashflowPlan
}

function stackedBucketsOf(
  targets: CashflowTarget[],
  index: number,
  { plan, ...options }: StackOptions,
): PlannedBucket[] {
  const { buckets } = plan ?? planCashflowBuckets(targets, options)
  return buckets.filter(
    (b) => b.contributors.length > 1 && b.contributors.includes(index),
  )
}

/** What the other contributors of `index`'s stacked bucket starting at `key` add; 0 when unstacked. */
export function othersAmountAt(
  targets: CashflowTarget[],
  key: string,
  index: number,
  options: StackOptions = {},
): number {
  const bucket = stackedBucketsOf(targets, index, options).find(
    (b) => b.start.replaceAll('-', '') === key,
  )
  if (!bucket) return 0
  return bucket.contributors.reduce((sum, other) => {
    const target = targets[other]
    if (other === index || !target) return sum
    return sum + (resolveAmount(target, key) ?? 0)
  }, 0)
}

/** Each stacked bucket `index` is in, as `YYYYMMDD` start key → the bucket's total. */
export function stackedTotals(
  targets: CashflowTarget[],
  index: number,
  options: StackOptions = {},
): Record<string, number> {
  const totals: Record<string, number> = {}
  for (const b of stackedBucketsOf(targets, index, options)) {
    if (b.amount !== null) totals[b.start.replaceAll('-', '')] = b.amount
  }
  return totals
}
